import { convertCommercialQuantity } from '../../utils/commercialQuantity';
import type { AllocationUnit } from '../../utils/dimensionConversion';
import { normalizeUnitCode } from '../../utils/units';
import { WAREHOUSE_CONVERTED_QUANTITY_MAX } from '../warehouse/warehouseQuantityConversion';
import type { WZCreateItemDraft } from './api/wzCreateApi';

export type WZBatchConversionTarget = 'base' | 'm2' | 'm3';

interface WZBatchConversionResult {
  items: WZCreateItemDraft[];
  convertedCount: number;
  skippedCount: number;
}

const commercialUnit = (unit: string): AllocationUnit => {
  const normalized = normalizeUnitCode(unit);
  return normalized === 'm2' || normalized === 'm3' ? normalized : 'pcs';
};

/**
 * Apply one commercial quantity basis to every WZ line without changing the
 * physical stock movement. Each line uses its own persisted dimensions.
 * Unsupported/missing-dimension lines stay byte-for-byte unchanged.
 */
export function convertWZItemsToTarget(
  items: WZCreateItemDraft[],
  target: WZBatchConversionTarget,
): WZBatchConversionResult {
  let convertedCount = 0;
  let skippedCount = 0;

  const nextItems = items.map((item) => {
    const physicalUnit = commercialUnit(item.unit);
    const pricingUnit = target === 'base' ? physicalUnit : target;
    const physicalQuantity = Number(item.quantity);

    if (!Number.isFinite(physicalQuantity) || physicalQuantity <= 0) {
      skippedCount += 1;
      return item;
    }

    if (pricingUnit === physicalUnit) {
      convertedCount += 1;
      return {
        ...item,
        conversionUnit: null,
        saleQuantity: physicalQuantity,
        saleUnit: physicalUnit,
      };
    }

    const converted = convertCommercialQuantity(
      physicalQuantity,
      physicalUnit,
      pricingUnit,
      item,
    );

    if (
      converted.error
      || converted.value == null
      || converted.value <= 0
      || converted.value > WAREHOUSE_CONVERTED_QUANTITY_MAX
    ) {
      skippedCount += 1;
      return item;
    }

    convertedCount += 1;
    return {
      ...item,
      conversionUnit: pricingUnit === 'pcs' ? null : pricingUnit,
      saleQuantity: converted.value,
      saleUnit: pricingUnit,
    };
  });

  return { items: nextItems, convertedCount, skippedCount };
}
